import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { PendingChip } from "../PendingChip";
import { COMPANY_CATEGORIES } from "../../data/companyCategories";
import { ensureHttpsUrl } from "../../utils/website";
import { validatePixelUrl } from "../../utils/pixelValidation";
import { getStatus, getStartUrl, disconnect as gaDisconnect, refreshProperties as gaRefreshProperties } from "../../services/gaService";
import { BillingMethodSection } from "../../features/support/BillingMethodSection";
import { BillingDetailsSection } from "../../features/support/BillingDetailsSection";
import { fetchAffiliatePlatforms } from "../../services/affiliatePlatformsService";

export function UserMenu({ gaOpenIntegrations = false, gaErrorFromUrl = false }) {
  const {
    user,
    session,
    signOut: authSignOut,
    effectiveAdvertiserId,
    updateFirstName,
    advertisers,
    currentAdvertiserId,
    setCurrentAdvertiserId,
    masqueradeAdvertiserId,
    userId,
    isAdvertiserAdmin,
    isSupportUser,
    canManageBilling,
  } = useAuth();
  const [open, setOpen] = useState(false);
  const [screen, setScreen] = useState(null);
  const menuRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inMenu && !inPanel) {
        setOpen(false);
        setScreen(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);
  // GA4 integration state
  const [gaStatus, setGaStatus] = useState(null); // null | { connected, provider_email, selected_property_id, properties }
  const [gaLoading, setGaLoading] = useState(false);
  const [gaError, setGaError] = useState(null);
  const [gaDisconnecting, setGaDisconnecting] = useState(false);
  const [gaShowPropertySelector, setGaShowPropertySelector] = useState(false);
  const [gaRefreshing, setGaRefreshing] = useState(false);
  const [gaSelectingProperty, setGaSelectingProperty] = useState(false);
  // 3rd-party tracking pixel state
  const [pixelUrl, setPixelUrl] = useState(null);
  const [pixelDraft, setPixelDraft] = useState("");
  const [pixelEditing, setPixelEditing] = useState(false);
  const [pixelLoading, setPixelLoading] = useState(false);
  const [pixelSavingState, setPixelSavingState] = useState("idle"); // idle | saving | saved | error
  const [pixelError, setPixelError] = useState(null);
  const [pixelConfirmingRemove, setPixelConfirmingRemove] = useState(false);

  const [billingRecord, setBillingRecord] = useState(null);
  const [billingRecordLoading, setBillingRecordLoading] = useState(false);
  const [billingRecordError, setBillingRecordError] = useState(null);

  const [affiliateReportingSavedPlatform, setAffiliateReportingSavedPlatform] = useState("");
  const [affiliateReportingPlatform, setAffiliateReportingPlatform] = useState("");
  const [affiliateReportingPlatforms, setAffiliateReportingPlatforms] = useState([]);
  const [affiliateReportingEditing, setAffiliateReportingEditing] = useState(false);
  const [affiliateReportingLoading, setAffiliateReportingLoading] = useState(false);
  const [affiliateReportingSaving, setAffiliateReportingSaving] = useState(false);
  const [affiliateReportingError, setAffiliateReportingError] = useState(null);
  const trimmedPixelDraft = pixelDraft.trim();
  const pixelValidation = useMemo(() => validatePixelUrl(trimmedPixelDraft), [trimmedPixelDraft]);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState(null);
  const [confirmingRemovalId, setConfirmingRemovalId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteBillingEnabled, setInviteBillingEnabled] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  const [roleUpdatingId, setRoleUpdatingId] = useState(null);
  const [roleError, setRoleError] = useState(null);
  const [billingUpdatingId, setBillingUpdatingId] = useState(null);
  const [billingError, setBillingError] = useState(null);
  const [editingMemberId, setEditingMemberId] = useState(null);

  // Settings panel state
  const [pendingRow, setPendingRow] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [draft, setDraft] = useState({ name: "", website: "", category: "" });
  const [advertiserUuid, setAdvertiserUuid] = useState("");
  const [uuidCopyState, setUuidCopyState] = useState("idle"); // idle | copied | error
const [savingState, setSavingState] = useState("idle"); // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [categoryInput, setCategoryInput] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const pendingRowId = useRef(null);

  // Fetch GA4 status whenever the integrations panel is opened
  const fetchGaStatus = useCallback(async () => {
    if (!effectiveAdvertiserId || !session?.access_token) return;
    setGaLoading(true);
    setGaError(null);
    try {
      const status = await getStatus(effectiveAdvertiserId, session.access_token);
      setGaStatus(status);
      // If connected but no property selected, show the property selector
      if (status.connected && !status.selected_property_id) {
        setGaShowPropertySelector(true);
      } else {
        setGaShowPropertySelector(false);
      }
    } catch (err) {
      setGaError(err?.message ?? "Failed to load Google Analytics status");
    } finally {
      setGaLoading(false);
    }
  }, [effectiveAdvertiserId, session?.access_token]);

  const fetchPixel = useCallback(async () => {
    if (!supabase || !effectiveAdvertiserId) return;
    setPixelLoading(true);
    setPixelError(null);
    try {
      const { data, error } = await supabase
        .from("advertiser_tracking_pixels")
        .select("pixel_url")
        .eq("advertiser_id", effectiveAdvertiserId)
        .maybeSingle();
      if (error) throw error;
      setPixelUrl(data?.pixel_url ?? null);
    } catch (err) {
      setPixelError(err?.message ?? "Failed to load tracking pixel");
    } finally {
      setPixelLoading(false);
    }
  }, [effectiveAdvertiserId]);

  // Single fetch of the advertisers row backing both BillingDetailsSection
  // and BillingMethodSection — they used to each query the same row
  // independently for their own column subset.
  const fetchBillingRecord = useCallback(async () => {
    if (!supabase || !effectiveAdvertiserId) return;
    setBillingRecordLoading(true);
    setBillingRecordError(null);
    try {
      const { data, error } = await supabase
        .from("advertisers")
        .select("billing_company_name, billing_address, accounting_email, billing_method, affiliate_platform, affiliate_platform_custom_name")
        .eq("id", effectiveAdvertiserId)
        .maybeSingle();
      if (error) throw error;
      setBillingRecord(data);
    } catch (err) {
      setBillingRecordError(err?.message ?? "Failed to load billing information");
    } finally {
      setBillingRecordLoading(false);
    }
  }, [effectiveAdvertiserId]);

  useEffect(() => {
    if (screen === "billing") fetchBillingRecord();
  }, [screen, fetchBillingRecord]);

  const affiliateReportingRequestId = useRef(0);

  const fetchAffiliateReportingPlatform = useCallback(async () => {
    if (!supabase || !effectiveAdvertiserId) return;
    const requestId = ++affiliateReportingRequestId.current;
    setAffiliateReportingLoading(true);
    setAffiliateReportingError(null);
    try {
      const [advertiserResult, platformsResult] = await Promise.allSettled([
        supabase
          .from("advertisers")
          .select("affiliate_reporting_platform")
          .eq("id", effectiveAdvertiserId)
          .maybeSingle(),
        fetchAffiliatePlatforms(),
      ]);
      if (requestId !== affiliateReportingRequestId.current) return;
      if (advertiserResult.status === "rejected" || advertiserResult.value.error) {
        setAffiliateReportingError(advertiserResult.value?.error?.message || advertiserResult.reason?.message || "Failed to load affiliate platform reporting");
        return;
      }
      const loaded = advertiserResult.value.data?.affiliate_reporting_platform ?? "";
      setAffiliateReportingSavedPlatform(loaded);
      setAffiliateReportingPlatform(loaded);
      setAffiliateReportingEditing(false);
      if (platformsResult.status === "fulfilled") {
        setAffiliateReportingPlatforms(platformsResult.value);
      } else {
        setAffiliateReportingPlatforms([]);
        setAffiliateReportingError(platformsResult.reason?.message || "Failed to load affiliate platform list");
        console.error("affiliate-platforms fetch failed:", platformsResult.reason);
      }
    } finally {
      if (requestId === affiliateReportingRequestId.current) setAffiliateReportingLoading(false);
    }
  }, [effectiveAdvertiserId]);

  const startAffiliateReportingEdit = () => {
    setAffiliateReportingPlatform(affiliateReportingSavedPlatform);
    setAffiliateReportingError(null);
    setAffiliateReportingEditing(true);
  };

  const cancelAffiliateReportingEdit = () => {
    setAffiliateReportingPlatform(affiliateReportingSavedPlatform);
    setAffiliateReportingError(null);
    setAffiliateReportingEditing(false);
  };

  const saveAffiliateReportingPlatform = async () => {
    if (!supabase || !effectiveAdvertiserId || !(canManageBilling || isAdvertiserAdmin)) return;
    setAffiliateReportingSaving(true);
    setAffiliateReportingError(null);
    try {
      const { error } = await supabase
        .from("advertisers")
        .update({ affiliate_reporting_platform: affiliateReportingPlatform || null })
        .eq("id", effectiveAdvertiserId);
      if (error) throw error;
      setAffiliateReportingSavedPlatform(affiliateReportingPlatform);
      setAffiliateReportingEditing(false);
    } catch (err) {
      setAffiliateReportingError(err?.message ?? "Failed to save affiliate platform reporting");
    } finally {
      setAffiliateReportingSaving(false);
    }
  };

  useEffect(() => {
    if (screen === "integrations") {
      fetchGaStatus();
      fetchPixel();
      fetchAffiliateReportingPlatform();
    }
  }, [screen, fetchGaStatus, fetchPixel, fetchAffiliateReportingPlatform]);


  const startPixelEdit = () => {
    setPixelDraft(pixelUrl ?? "");
    setPixelSavingState("idle");
    setPixelError(null);
    setPixelEditing(true);
  };

  const cancelPixelEdit = () => {
    setPixelEditing(false);
    setPixelDraft("");
    setPixelError(null);
  };

  const triggerPixelSync = async (advertiserId) => {
    if (!supabase) return;
    try {
      const { error } = await supabase.functions.invoke("trigger-backend-job", {
        body: { task_name: "sync_supabase_postgres", advertiser_id: advertiserId },
      });
      if (error) throw error;
    } catch (err) {
      // Pixel row already saved/removed — sync is a background propagation step,
      // so a failure here shouldn't block or alarm the user.
      console.error("Failed to trigger pixel sync:", err?.message ?? err);
    }
  };

  const savePixel = async () => {
    if (!supabase || !effectiveAdvertiserId) return;
    if (!trimmedPixelDraft || pixelValidation.hasError) {
      setPixelError(pixelValidation.errorMessage || "Please enter a pixel URL");
      return;
    }
    setPixelSavingState("saving");
    setPixelError(null);
    try {
      const { error } = await supabase
        .from("advertiser_tracking_pixels")
        .upsert({ advertiser_id: effectiveAdvertiserId, pixel_url: trimmedPixelDraft }, { onConflict: "advertiser_id" });
      if (error) throw error;
      setPixelUrl(trimmedPixelDraft);
      setPixelSavingState("saved");
      setPixelEditing(false);
      triggerPixelSync(effectiveAdvertiserId);
    } catch (err) {
      setPixelSavingState("error");
      setPixelError(err?.message ?? "Failed to save tracking pixel");
    }
  };

  const removePixel = async () => {
    if (!supabase || !effectiveAdvertiserId) return;
    setPixelConfirmingRemove(false);
    setPixelSavingState("saving");
    setPixelError(null);
    try {
      const { error } = await supabase
        .from("advertiser_tracking_pixels")
        .delete()
        .eq("advertiser_id", effectiveAdvertiserId);
      if (error) throw error;
      setPixelUrl(null);
      setPixelSavingState("idle");
      triggerPixelSync(effectiveAdvertiserId);
    } catch (err) {
      setPixelSavingState("error");
      setPixelError(err?.message ?? "Failed to remove tracking pixel");
    }
  };

  // Auto-open integrations panel when ga= URL param is detected by AuthGate.
  useEffect(() => {
    if (gaOpenIntegrations) {
      setOpen(true);
      setScreen("integrations");
      if (gaErrorFromUrl) {
        setGaError("Google Analytics connection failed. Please try again.");
      }
    }
  }, [gaOpenIntegrations, gaErrorFromUrl]);

  const resetInviteForm = () => {
    setInviteEmail("");
    setInviteFirstName("");
    setInviteLastName("");
    setInviteRole("member");
    setInviteBillingEnabled(false);
    setInviteError(null);
  };

  const closeInviteModal = () => {
    setInviteModalOpen(false);
    resetInviteForm();
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "User";
  const displayEmail = user?.email || "";
  const mapTeamRow = (row) => ({
    ...row.users,
    role: row.role,
    billing_enabled: !!row.billing_enabled,
  });

  const fetchTeam = useCallback(() => {
    if (effectiveAdvertiserId == null || !supabase) return Promise.resolve({ data: [], error: null });
    return supabase
      .from("user_advertisers")
      .select("role, billing_enabled, users!inner(id, first_name, last_name, email)")
      .eq("advertiser_id", effectiveAdvertiserId)
      .order("created_at", { ascending: true })
      .then(({ data, error }) => ({ data: (data || []).map(mapTeamRow), error }));
  }, [effectiveAdvertiserId]);

  // Fetch team members when Team panel is open and we have an advertiser.
  // Query via user_advertisers so secondary members (is_primary=false) are included.
  useEffect(() => {
    if (screen !== "team" || effectiveAdvertiserId == null || !supabase) return;
    setTeamLoading(true);
    setTeamError(null);
    setEditingMemberId(null);
    setConfirmingRemovalId(null);
    fetchTeam().then(({ data, error }) => {
      setTeamLoading(false);
      if (error) {
        setTeamError(error.message || "Failed to load team");
        setTeamMembers([]);
      } else {
        setTeamMembers(data);
      }
    });
  }, [screen, effectiveAdvertiserId, fetchTeam]);

  // Fetch advertiser baseline + pending row when Settings panel opens
  useEffect(() => {
    if (screen !== "settings" || effectiveAdvertiserId == null || !supabase) return;
    let cancelled = false;
    setSettingsLoading(true);
    setSaveError(null);
    setSuggestions([]);
    (async () => {
      const [advertiserRes, pendingRes] = await Promise.all([
        supabase
          .from("advertisers")
          .select("name, website, category, uuid")
          .eq("id", effectiveAdvertiserId)
          .maybeSingle(),
        supabase
          .from("advertiser_pending_settings")
          .select("id, name, website, category, proposed_by")
          .eq("advertiser_id", effectiveAdvertiserId)
          .maybeSingle(),
      ]);
      if (cancelled) return;

      const adv = advertiserRes.data;
      const pending = pendingRes.data;
      setAdvertiserUuid(adv?.uuid ?? "");

      setPendingRow(pending ?? null);
      pendingRowId.current = pending?.id ?? null;

      // Hydrate draft from pending if it exists, else from advertiser baseline
      const catValue = pending?.category ?? adv?.category ?? "";
      const catTitle = COMPANY_CATEGORIES.find(c => c.value === catValue)?.title ?? catValue;

      if (pending) {
        setDraft({
          name:     pending.name     ?? adv?.name     ?? "",
          website:  pending.website  ?? adv?.website  ?? "",
          category: catValue,
        });
      } else {
        setDraft({
          name:     adv?.name     ?? "",
          website:  adv?.website  ?? "",
          category: catValue,
        });
      }
      setCategoryInput(catTitle);

      setSettingsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [screen, effectiveAdvertiserId]);

  const refetchTeam = useCallback(() => {
    fetchTeam().then(({ data, error }) => { if (!error) setTeamMembers(data); });
  }, [fetchTeam]);

  const handleSettingsSave = async () => {
    if (!supabase || effectiveAdvertiserId == null) {
      setSaveError("Connection unavailable");
      setSavingState("error");
      return;
    }
    setSavingState("saving");
    setSaveError(null);

    const normalizedWebsite = ensureHttpsUrl(draft.website);
    if (normalizedWebsite !== draft.website) {
      setDraft((d) => ({ ...d, website: normalizedWebsite }));
    }

    const payload = {
      name:     draft.name     || null,
      website:  normalizedWebsite || null,
      category: draft.category || null,
    };

    let error = null;

    if (isSupportUser) {
      // Admins write directly to advertisers — no approval needed
      const res = await supabase
        .from("advertisers")
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq("id", effectiveAdvertiserId);
      error = res.error;
    } else if (pendingRowId.current) {
      // UPDATE existing pending row
      const res = await supabase
        .from("advertiser_pending_settings")
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq("id", pendingRowId.current);
      error = res.error;
    } else {
      // INSERT new pending row — .select("id").maybeSingle() required so
      // Supabase v2 returns data (otherwise data is null and ref stays unset,
      // causing a UNIQUE constraint violation on the next save attempt).
      const res = await supabase
        .from("advertiser_pending_settings")
        .insert({
          advertiser_id: effectiveAdvertiserId,
          proposed_by: user?.id,
          ...payload,
        })
        .select("id")
        .maybeSingle();
      error = res.error;
      if (!error && res.data) {
        pendingRowId.current = res.data.id ?? null;
      }
      if (!error) {
        setPendingRow({ ...payload, id: pendingRowId.current });
      }
    }

    if (error) {
      setSavingState("error");
      setSaveError(error.message || "Failed to save settings");
      return;
    }
    setSavingState("saved");
    setTimeout(() => setSavingState("idle"), 2000);
  };

  const copyTextToClipboard = async (text) => {
    if (!text) return false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const tempTextArea = document.createElement("textarea");
        tempTextArea.value = text;
        tempTextArea.setAttribute("readonly", "");
        tempTextArea.style.position = "absolute";
        tempTextArea.style.left = "-9999px";
        document.body.appendChild(tempTextArea);
        tempTextArea.select();
        document.execCommand("copy");
        document.body.removeChild(tempTextArea);
      }
      return true;
    } catch {
      return false;
    }
  };

  const handleCopyAdvertiserUuid = async () => {
    const success = await copyTextToClipboard(advertiserUuid);
    setUuidCopyState(success ? "copied" : "error");
    setTimeout(() => setUuidCopyState("idle"), 1800);
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!isAdvertiserAdmin) {
      setInviteError("Only advertiser admins can invite members.");
      return;
    }
    setInviteError(null);
    setInviteLoading(true);
    const email = inviteEmail.trim().toLowerCase();
    const firstName = (inviteFirstName || "").trim() || "Invited";
    const lastName = (inviteLastName || "").trim() || "User";
    if (!email) {
      setInviteError("Email is required");
      setInviteLoading(false);
      return;
    }
    if (effectiveAdvertiserId == null) {
      setInviteError("No active advertiser");
      setInviteLoading(false);
      return;
    }

    // Dual path: look up an existing users row first; if found, just attach
    // them to the active advertiser via user_advertisers. Otherwise fall back
    // to the legacy users insert (the sync trigger will populate the junction).
    const { data: existing, error: lookupError } = await supabase
      .from("users")
      .select("id")
      .eq("email", email)
      .maybeSingle();
    if (lookupError) {
      setInviteLoading(false);
      setInviteError(lookupError.message || "Failed to look up user");
      return;
    }

    const role = inviteRole;
    const billing_enabled = !!inviteBillingEnabled;

    let writeError = null;
    if (existing?.id) {
      const { error } = await supabase.from("user_advertisers").insert({
        user_id: existing.id,
        advertiser_id: effectiveAdvertiserId,
        role,
        billing_enabled,
        is_primary: false,
      });
      // Already a member of this advertiser → no-op success.
      if (error && error.code !== "23505") writeError = error;
    } else {
      // Legacy path: inserting a new user creates the membership via trigger
      // with default role=member/billing_enabled=false. Apply the chosen
      // values via a follow-up update when they differ from defaults.
      const { error: insertError } = await supabase.from("users").insert({
        email,
        first_name: firstName,
        last_name: lastName,
        advertiser_id: effectiveAdvertiserId,
      });
      writeError = insertError;

      if (!writeError && (role !== "member" || billing_enabled)) {
        const { data: created } = await supabase
          .from("users")
          .select("id")
          .eq("email", email)
          .maybeSingle();
        if (created?.id) {
          await supabase
            .from("user_advertisers")
            .update({ role, billing_enabled })
            .eq("user_id", created.id)
            .eq("advertiser_id", effectiveAdvertiserId);
        }
      }
    }

    setInviteLoading(false);
    if (writeError) {
      setInviteError(writeError.message || "Failed to invite member");
      return;
    }
    closeInviteModal();
    refetchTeam();
  };

  const adminCount = teamMembers.filter((m) => m.role === "admin").length;
  const isLastAdmin = (m) => m.role === "admin" && adminCount <= 1;

  const handleRemove = async (memberId) => {
    if (!supabase || effectiveAdvertiserId == null || !isAdvertiserAdmin) return;
    setRemovingId(memberId);
    setRemoveError(null);
    const { error } = await supabase
      .from("user_advertisers")
      .delete()
      .eq("user_id", memberId)
      .eq("advertiser_id", effectiveAdvertiserId);
    setRemovingId(null);
    setConfirmingRemovalId(null);
    if (error) {
      setRemoveError(error.message || "Failed to remove member");
      return;
    }
    setTeamMembers((prev) => prev.filter((m) => m.id !== memberId));
    setEditingMemberId((current) => (current === memberId ? null : current));
  };

  // Shared optimistic-patch + rollback flow for role and billing toggles.
  const updateMember = async (memberId, patch, { setUpdatingId, setError, errorFallback }) => {
    if (!supabase || effectiveAdvertiserId == null || !isAdvertiserAdmin) return;
    setError(null);
    setUpdatingId(memberId);
    const previous = teamMembers;
    setTeamMembers((current) => current.map((m) => (m.id === memberId ? { ...m, ...patch } : m)));
    const { error } = await supabase
      .from("user_advertisers")
      .update(patch)
      .eq("user_id", memberId)
      .eq("advertiser_id", effectiveAdvertiserId);
    setUpdatingId(null);
    if (error) {
      setError(error.message || errorFallback);
      setTeamMembers(previous);
    }
  };

  const handleRoleChange = (memberId, nextRole) =>
    updateMember(memberId, { role: nextRole }, {
      setUpdatingId: setRoleUpdatingId,
      setError: setRoleError,
      errorFallback: "Failed to update role",
    });

  const handleBillingAccessChange = (memberId, nextEnabled) =>
    updateMember(memberId, { billing_enabled: nextEnabled }, {
      setUpdatingId: setBillingUpdatingId,
      setError: setBillingError,
      errorFallback: "Failed to update billing access",
    });

  const inviteSubmitButtonStyle = {
    padding: "9px 16px",
    background: "#F90091",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: inviteLoading ? "wait" : "pointer",
    opacity: inviteLoading ? 0.7 : 1,
  };

  // Inline SVG icons matching the Figma design
  const icons = {
    profile: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    billing: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
      </svg>
    ),
    team: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    integrations: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
      </svg>
    ),
    settings: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    ),
    signout: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
      </svg>
    ),
  };

  const menuItems = [
    { icon: icons.profile,      label:"Profile",       key:"profile" },
    { icon: icons.billing,      label:"Billing",       key:"billing" },
    { icon: icons.team,         label:"Team Members",  key:"team" },
    { icon: icons.integrations, label:"Integrations",  key:"integrations" },
    { icon: icons.settings, label:"Settings", key:"settings" },
    { icon: icons.signout,      label:"Sign Out",      key:"signout",       danger:true },
  ];

  const panels = {
    profile: {
      title: "Profile",
      content: (
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:24 }}>
            <div style={{ width:56, height:56, borderRadius:"50%", background:"#534f60", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, color:"#fff" }}>{(displayName || "U").charAt(0).toUpperCase()}</div>
            <div>
              <div style={{ fontWeight:600, color:"#151515", fontSize:16, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>{displayName}</div>
              <div style={{ color:"rgba(21,21,21,0.6)", fontSize:13 }}>{displayEmail || "—"}</div>
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Full Name</div>
            <input
              value={nameInput}
              onChange={e => { setNameInput(e.target.value); setNameError(null); }}
              style={{ width:"100%", boxSizing:"border-box", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"9px 12px", color:"#151515", fontSize:13, outline:"none" }}
            />
          </div>
          {[{label:"Email", value:displayEmail},{label:"Role", value:"Admin"}].map(f => (
            <div key={f.label} style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{f.label}</div>
              <div style={{ background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"9px 12px", color:"#3E3E3E", fontSize:13 }}>{f.value}</div>
            </div>
          ))}
          {nameError && <div style={{ color:"#e53935", fontSize:12, marginBottom:8 }}>{nameError}</div>}
          <button
            disabled={savingName}
            onClick={async () => {
              if (!nameInput.trim()) { setNameError("Name cannot be empty."); return; }
              setSavingName(true);
              const { error } = await updateFirstName(nameInput);
              setSavingName(false);
              if (error) setNameError(error.message);
            }}
            style={{ width:"100%", marginTop:8, padding:"10px 0", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor: savingName ? "default" : "pointer", opacity: savingName ? 0.7 : 1 }}>
            {savingName ? "Saving…" : "Save Changes"}
          </button>
        </div>
      )
    },
    billing: {
      title: "Billing",
      content: (
        <div>
          {billingRecordError && (
            <div style={{ marginBottom:12, padding:10, background:"#FFF0F0", border:"1px solid #fca5a5", borderRadius:8, fontSize:12, color:"#e53935" }}>{billingRecordError}</div>
          )}
          <BillingDetailsSection advertiserId={effectiveAdvertiserId} canManageBilling={canManageBilling} initialData={billingRecord} loading={billingRecordLoading} />
          <BillingMethodSection advertiserId={effectiveAdvertiserId} isSupportUser={isSupportUser} initialData={billingRecord} loading={billingRecordLoading} />
        </div>
      )
    },
    team: {
      title: "Team Members",
      content: (
        <div>
          {(teamError || removeError || roleError || billingError) && (
            <div style={{ marginBottom:12, padding:10, background:"#FFF0F0", border:"1px solid #fca5a5", borderRadius:8, fontSize:12, color:"#e53935" }}>{removeError || roleError || billingError || teamError}</div>
          )}
          {teamLoading ? (
            <div style={{ color:"#626262", fontSize:13, padding:"12px 0" }}>Loading team…</div>
          ) : effectiveAdvertiserId == null ? (
            <div style={{ color:"#626262", fontSize:13, padding:"12px 0" }}>No advertiser linked to your account.</div>
          ) : (
            <>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {teamMembers.map(m => {
                  const name = [m.first_name, m.last_name].filter(Boolean).join(" ") || m.email?.split("@")[0] || "—";
                  const avatar = (m.first_name || m.email || "?").charAt(0).toUpperCase();
                  const isCurrentUser = userId ? m.id === userId : m.email === displayEmail;
                  const lastAdmin = isLastAdmin(m);
                  const roleLabel = m.role === "viewer" ? "Viewer" : m.role === "admin" ? "Admin" : "Member";
                  const isConfirming = confirmingRemovalId === m.id;
                  const canEdit = isAdvertiserAdmin && !isCurrentUser;
                  const isEditing = canEdit && editingMemberId === m.id;

                  const roleChip = (
                    <span
                      style={{
                        fontSize:10,
                        fontWeight:500,
                        padding:"2px 8px",
                        borderRadius:20,
                        background: m.role === "admin" ? "#FFE9F6" : "#F5F5F5",
                        color: m.role === "admin" ? "#F90091" : "#3E3E3E",
                        border:`1px solid ${m.role === "admin" ? "#F9009133" : "#E2E2E2"}`,
                        whiteSpace:"nowrap",
                      }}
                    >
                      {roleLabel}
                    </span>
                  );

                  const billingChip = m.billing_enabled ? (
                    <span
                      style={{
                        fontSize:10,
                        fontWeight:500,
                        padding:"2px 8px",
                        borderRadius:20,
                        background:"#FFF5FB",
                        color:"#F90091",
                        border:"1px solid #F9009133",
                        whiteSpace:"nowrap",
                      }}
                    >
                      Billing
                    </span>
                  ) : null;

                  const toggleEditing = () =>
                    setEditingMemberId((prev) => (prev === m.id ? null : m.id));

                  return (
                    <div
                      key={m.id}
                      style={{
                        background:"#FFFFFF",
                        border:`1px solid ${isEditing ? "#E2E2E2" : "#EAEAEA"}`,
                        borderRadius:10,
                        padding:isEditing ? 10 : 8,
                        display:"flex",
                        flexDirection:"column",
                        gap:0,
                      }}
                    >
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div
                          style={{
                            width:28,
                            height:28,
                            borderRadius:"50%",
                            background:"#534f60",
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"center",
                            fontSize:12,
                            fontWeight:600,
                            color:"#fff",
                            flexShrink:0,
                          }}
                        >
                          {avatar}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                            <span
                              style={{
                                color:"#151515",
                                fontSize:13,
                                fontWeight:600,
                                lineHeight:1.2,
                                overflow:"hidden",
                                textOverflow:"ellipsis",
                                whiteSpace:"nowrap",
                                maxWidth:"100%",
                              }}
                            >
                              {name}
                            </span>
                            {isCurrentUser && (
                              <span
                                style={{
                                  fontSize:9,
                                  fontWeight:600,
                                  letterSpacing:0.4,
                                  textTransform:"uppercase",
                                  color:"#626262",
                                  background:"#F5F5F5",
                                  padding:"1px 5px",
                                  borderRadius:5,
                                }}
                              >
                                You
                              </span>
                            )}
                            {roleChip}
                            {billingChip}
                          </div>
                          <div
                            style={{
                              color:"#7b7b7b",
                              fontSize:11,
                              marginTop:1,
                              overflow:"hidden",
                              textOverflow:"ellipsis",
                              whiteSpace:"nowrap",
                            }}
                            title={m.email}
                          >
                            {m.email}
                          </div>
                        </div>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={toggleEditing}
                            aria-label={isEditing ? `Close permissions for ${m.email}` : `Edit permissions for ${m.email}`}
                            aria-expanded={isEditing}
                            title={isEditing ? "Close" : "Edit permissions"}
                            style={{
                              width:28,
                              height:28,
                              display:"inline-flex",
                              alignItems:"center",
                              justifyContent:"center",
                              background: isEditing ? "#F5F5F5" : "transparent",
                              border:"none",
                              borderRadius:6,
                              color:"#626262",
                              cursor:"pointer",
                              flexShrink:0,
                              transition:"background 120ms ease, color 120ms ease",
                            }}
                          >
                            {isEditing ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="3"/>
                                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                              </svg>
                            )}
                          </button>
                        )}
                      </div>

                      {isEditing && (
                        <div
                          style={{
                            marginTop:10,
                            paddingTop:10,
                            borderTop:"1px dashed #EAEAEA",
                            display:"flex",
                            flexDirection:"column",
                            gap:8,
                          }}
                        >
                          <div
                            style={{
                              display:"flex",
                              alignItems:"center",
                              gap:6,
                              flexWrap:"wrap",
                            }}
                          >
                            <select
                              aria-label={`Role for ${m.email}`}
                              value={m.role}
                              disabled={roleUpdatingId === m.id || lastAdmin}
                              onChange={(e) => handleRoleChange(m.id, e.target.value)}
                              style={{
                                flex:"1 1 auto",
                                minWidth:96,
                                height:26,
                                padding:"0 24px 0 8px",
                                border:"1px solid #E2E2E2",
                                borderRadius:6,
                                fontSize:11,
                                fontFamily:"inherit",
                                background:`#FFFFFF url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%23626262' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M1 1.5L6 6.5L11 1.5'/></svg>") no-repeat right 8px center / 9px 5px`,
                                appearance:"none",
                                WebkitAppearance:"none",
                                MozAppearance:"none",
                                color:"#151515",
                                cursor: roleUpdatingId === m.id || lastAdmin ? "not-allowed" : "pointer",
                                opacity: roleUpdatingId === m.id ? 0.6 : 1,
                              }}
                            >
                              <option value="admin">Admin</option>
                              <option value="member">Member</option>
                              <option value="viewer">Viewer</option>
                            </select>

                            <label
                              htmlFor={`billing-${m.id}`}
                              style={{
                                display:"inline-flex",
                                alignItems:"center",
                                gap:5,
                                height:26,
                                padding:"0 8px",
                                border:`1px solid ${m.billing_enabled ? "#F9009133" : "#E2E2E2"}`,
                                borderRadius:6,
                                fontSize:11,
                                color: m.billing_enabled ? "#F90091" : "#3E3E3E",
                                background: m.billing_enabled ? "#FFF5FB" : "#FFFFFF",
                                cursor: billingUpdatingId === m.id ? "wait" : "pointer",
                                userSelect:"none",
                                opacity: billingUpdatingId === m.id ? 0.6 : 1,
                                transition:"background 150ms ease, border-color 150ms ease, color 150ms ease",
                              }}
                            >
                              <input
                                id={`billing-${m.id}`}
                                type="checkbox"
                                aria-label={`Billing access for ${m.email}`}
                                checked={!!m.billing_enabled}
                                disabled={billingUpdatingId === m.id}
                                onChange={(e) => handleBillingAccessChange(m.id, e.target.checked)}
                                style={{
                                  width:12,
                                  height:12,
                                  margin:0,
                                  accentColor:"#F90091",
                                  cursor:"inherit",
                                }}
                              />
                              Billing
                            </label>

                            {!isConfirming && (
                              <button
                                type="button"
                                onClick={() => setConfirmingRemovalId(m.id)}
                                disabled={lastAdmin || removingId === m.id}
                                title={lastAdmin ? "Cannot remove the last admin" : "Remove member"}
                                aria-label="Remove"
                                style={{
                                  marginLeft:"auto",
                                  width:26,
                                  height:26,
                                  display:"inline-flex",
                                  alignItems:"center",
                                  justifyContent:"center",
                                  background:"transparent",
                                  border:`1px solid ${lastAdmin ? "#EAEAEA" : "#fca5a5"}`,
                                  borderRadius:6,
                                  color: lastAdmin ? "#CFCFCF" : "#e53935",
                                  cursor: lastAdmin ? "not-allowed" : "pointer",
                                  transition:"background 120ms ease, border-color 120ms ease",
                                }}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                  <path d="M10 11v6"/>
                                  <path d="M14 11v6"/>
                                  <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                                </svg>
                              </button>
                            )}
                          </div>

                          {lastAdmin && (
                            <div style={{ fontSize:10, color:"#7b7b7b" }}>
                              Last admin. Promote another member before removing.
                            </div>
                          )}

                          {isConfirming && (
                            <div
                              style={{
                                display:"flex",
                                alignItems:"center",
                                justifyContent:"space-between",
                                gap:6,
                                padding:"6px 8px",
                                background:"#FFF5F5",
                                border:"1px solid #fca5a5",
                                borderRadius:6,
                                fontSize:11,
                              }}
                            >
                              <span style={{ color:"#7b1e1c" }}>Remove from team?</span>
                              <div style={{ display:"flex", gap:6 }}>
                                <button
                                  type="button"
                                  onClick={() => setConfirmingRemovalId(null)}
                                  style={{
                                    padding:"4px 8px",
                                    background:"#FFFFFF",
                                    border:"1px solid #E2E2E2",
                                    borderRadius:6,
                                    fontSize:11,
                                    color:"#3E3E3E",
                                    cursor:"pointer",
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemove(m.id)}
                                  disabled={removingId === m.id}
                                  style={{
                                    padding:"4px 8px",
                                    background:"#e53935",
                                    border:"none",
                                    borderRadius:6,
                                    fontSize:11,
                                    fontWeight:600,
                                    color:"#fff",
                                    cursor: removingId === m.id ? "wait" : "pointer",
                                    opacity: removingId === m.id ? 0.7 : 1,
                                  }}
                                >
                                  {removingId === m.id ? "Removing…" : "Yes, remove"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {isAdvertiserAdmin && (
                <button
                  onClick={() => setInviteModalOpen(true)}
                  style={{ width:"100%", marginTop:16, padding:"10px 0", background:"transparent", color:"#F90091", border:"1px solid #F9009166", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer" }}
                >+ Invite Member</button>
              )}
            </>
          )}
          {inviteModalOpen && (
            <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
              <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:16, width:"100%", maxWidth:400, boxShadow:"0 20px 60px rgba(0,0,0,0.15)" }}>
                <div style={{ padding:"18px 20px", borderBottom:"1px solid #E2E2E2", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontWeight:600, color:"#151515", fontSize:15 }}>Invite team member</span>
                  <button onClick={closeInviteModal} style={{ background:"none", border:"none", color:"#626262", fontSize:18, cursor:"pointer" }}>✕</button>
                </div>
                <form onSubmit={handleInviteMember} style={{ padding:20 }}>
                  {inviteError && (
                    <div style={{ marginBottom:12, padding:10, background:"#FFF0F0", border:"1px solid #fca5a5", borderRadius:8, fontSize:12, color:"#e53935" }}>{inviteError}</div>
                  )}
                  <div style={{ marginBottom:14 }}>
                    <label style={{ display:"block", fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Email</label>
                    <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required placeholder="colleague@company.com" style={{ width:"100%", boxSizing:"border-box", padding:"9px 12px", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, color:"#151515", fontSize:13, outline:"none" }} />
                  </div>
                  <div style={{ display:"flex", gap:12, marginBottom:14 }}>
                    <div style={{ flex:1 }}>
                      <label style={{ display:"block", fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>First name</label>
                      <input type="text" value={inviteFirstName} onChange={e => setInviteFirstName(e.target.value)} placeholder="Jane" style={{ width:"100%", boxSizing:"border-box", padding:"9px 12px", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, color:"#151515", fontSize:13, outline:"none" }} />
                    </div>
                    <div style={{ flex:1 }}>
                      <label style={{ display:"block", fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Last name</label>
                      <input type="text" value={inviteLastName} onChange={e => setInviteLastName(e.target.value)} placeholder="Doe" style={{ width:"100%", boxSizing:"border-box", padding:"9px 12px", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, color:"#151515", fontSize:13, outline:"none" }} />
                    </div>
                  </div>
                  <div style={{ marginBottom:14 }}>
                    <label htmlFor="invite-role" style={{ display:"block", fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Role</label>
                    <select
                      id="invite-role"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      style={{
                        width:"100%",
                        boxSizing:"border-box",
                        padding:"9px 32px 9px 12px",
                        background:`#FAFAFA url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%23626262' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M1 1.5L6 6.5L11 1.5'/></svg>") no-repeat right 12px center / 10px 6px`,
                        border:"1px solid #E2E2E2",
                        borderRadius:8,
                        color:"#151515",
                        fontSize:13,
                        outline:"none",
                        appearance:"none",
                        WebkitAppearance:"none",
                        MozAppearance:"none",
                        cursor:"pointer",
                      }}
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </div>
                  <label
                    htmlFor="invite-billing"
                    style={{
                      display:"flex",
                      alignItems:"center",
                      gap:8,
                      marginBottom:18,
                      padding:"10px 12px",
                      background: inviteBillingEnabled ? "#FFF5FB" : "#FAFAFA",
                      border:`1px solid ${inviteBillingEnabled ? "#F9009133" : "#E2E2E2"}`,
                      borderRadius:8,
                      fontSize:13,
                      color: inviteBillingEnabled ? "#F90091" : "#3E3E3E",
                      cursor:"pointer",
                      userSelect:"none",
                      transition:"background 150ms ease, border-color 150ms ease, color 150ms ease",
                    }}
                  >
                    <input
                      id="invite-billing"
                      type="checkbox"
                      checked={inviteBillingEnabled}
                      onChange={(e) => setInviteBillingEnabled(e.target.checked)}
                      style={{ width:14, height:14, margin:0, accentColor:"#F90091", cursor:"inherit" }}
                    />
                    Grant billing access
                  </label>
                  <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                    <button type="button" onClick={closeInviteModal} style={{ padding:"9px 16px", background:"#F5F5F5", color:"#3E3E3E", border:"1px solid #E2E2E2", borderRadius:8, fontSize:13, cursor:"pointer" }}>Cancel</button>
                    <button type="submit" disabled={inviteLoading} style={inviteSubmitButtonStyle}>{inviteLoading ? "Sending…" : "Send invite"}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )
    },
    integrations: {
      title: "Integrations",
      content: (
        <div>
          <div style={{
            background: "#FFFFFF",
            border: `1px solid ${gaStatus?.connected && !!gaStatus?.selected_property_id ? "#F9009133" : "#E2E2E2"}`,
            borderRadius: 12,
            overflow: "hidden",
          }}>
            {/* Card header row */}
            <div style={{ padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
              {/* GA4 icon */}
              <div style={{
                width: 42, height: 42, borderRadius: 10,
                background: "#FFF7ED", border: "1px solid #FED7AA",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="22" height="22" viewBox="0 0 22 21" fill="none">
                  <rect x="0" y="13" width="6" height="8" rx="2" fill="#E37400"/>
                  <rect x="8" y="7" width="6" height="14" rx="2" fill="#F9AB00"/>
                  <rect x="16" y="0" width="6" height="21" rx="2" fill="#E37400" opacity="0.55"/>
                </svg>
              </div>

              {/* Name + status */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, color: "#151515", fontSize: 14, lineHeight: 1.2 }}>Google Analytics 4</span>
                  <span style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase",
                    color: "#7b7b7b", background: "#F5F5F5", padding: "2px 6px", borderRadius: 4,
                  }}>Analytics</span>
                </div>
                {gaLoading ? (
                  <div style={{ height: 14, width: 120, background: "#F0F0F0", borderRadius: 4 }} />
                ) : gaStatus?.connected && !!gaStatus?.selected_property_id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", display: "inline-block", flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#3E3E3E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {gaStatus.provider_email}
                    </span>
                  </div>
                ) : gaStatus?.connected && !gaStatus?.selected_property_id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#F59E0B", display: "inline-block", flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#3E3E3E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {gaStatus.provider_email}
                    </span>
                  </div>
                ) : gaError ? (
                  <span style={{ fontSize: 11, color: "#e53935" }}>{gaError}</span>
                ) : null}
              </div>

              {/* Action button */}
              <div style={{ flexShrink: 0, paddingTop: 2 }}>
                {gaLoading ? (
                  <div style={{ width: 125, height: 30, background: "#F0F0F0", borderRadius: 8 }} />
                ) : gaError ? (
                  <button
                    onClick={fetchGaStatus}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E2E2", fontSize: 12, fontWeight: 500, cursor: "pointer", background: "#FAFAFA", color: "#3E3E3E", minWidth: 125, whiteSpace: "nowrap" }}
                  >
                    Retry
                  </button>
                ) : gaStatus?.connected && !!gaStatus?.selected_property_id ? (
                  <button
                    onClick={async () => {
                      setGaDisconnecting(true);
                      try {
                        await gaDisconnect(effectiveAdvertiserId, session?.access_token);
                        await fetchGaStatus();
                      } catch (err) {
                        setGaError(err?.message ?? "Failed to disconnect");
                      } finally { setGaDisconnecting(false); }
                    }}
                    disabled={gaDisconnecting}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #151515", fontSize: 12, fontWeight: 600, cursor: gaDisconnecting ? "default" : "pointer", background: "none", color: "#151515", opacity: gaDisconnecting ? 0.6 : 1, minWidth: 125, whiteSpace: "nowrap" }}
                  >
                    {gaDisconnecting ? "Disconnecting…" : "Disconnect"}
                  </button>
                ) : gaStatus?.connected && !gaStatus?.selected_property_id ? (
                  <button
                    onClick={async () => {
                      setGaDisconnecting(true);
                      try {
                        await gaDisconnect(effectiveAdvertiserId, session?.access_token);
                        await fetchGaStatus();
                      } catch (err) {
                        setGaError(err?.message ?? "Failed to disconnect");
                      } finally { setGaDisconnecting(false); }
                    }}
                    disabled={gaDisconnecting}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E2E2", fontSize: 12, fontWeight: 500, cursor: gaDisconnecting ? "default" : "pointer", background: "#FAFAFA", color: "#626262", opacity: gaDisconnecting ? 0.6 : 1, minWidth: 125, whiteSpace: "nowrap" }}
                  >
                    {gaDisconnecting ? "Cancelling…" : "Cancel setup"}
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      try {
                        const url = await getStartUrl(effectiveAdvertiserId, session?.access_token);
                        window.location.assign(url);
                      } catch (err) {
                        setGaError(err?.message ?? "Failed to initiate Google connection");
                      }
                    }}
                    style={{ padding: "6px 14px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", background: "#F90091", color: "#fff", minWidth: 125, whiteSpace: "nowrap" }}
                  >
                    Connect
                  </button>
                )}
              </div>
            </div>

            {/* Description — disconnected state only, full card width */}
            {!gaLoading && !gaError && !gaStatus?.connected && (
              <div style={{ borderTop: "1px solid #F0F0F0", padding: "10px 16px" }}>
                <span style={{ fontSize: 12, color: "#7b7b7b", lineHeight: 1.5 }}>
                  Import GA4 conversion goals to validate podcast-driven site activity.
                </span>
              </div>
            )}

            {/* Pending instruction — full card width, shown before property selector */}
            {!gaLoading && gaStatus?.connected && !gaStatus?.selected_property_id && (
              <div style={{ borderTop: "1px solid #F0F0F0", padding: "10px 16px" }}>
                <span style={{ fontSize: 12, color: "#7b7b7b", lineHeight: 1.5 }}>
                  Select a GA4 property to complete your setup.
                </span>
              </div>
            )}

            {/* Property section — shown when connected */}
            {gaStatus?.connected && (
              <div style={{ borderTop: "1px solid #F0F0F0", padding: "12px 16px" }}>
                {gaShowPropertySelector ? (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: !gaStatus?.selected_property_id ? "#F59E0B" : "#626262", textTransform: "uppercase", letterSpacing: 0.8 }}>
                        {!gaStatus?.selected_property_id ? "Step 2 — Select a GA4 Property" : "Select a Property"}
                      </span>
                      {gaStatus.selected_property_id && (
                        <button
                          onClick={() => setGaShowPropertySelector(false)}
                          style={{ fontSize: 11, color: "#7b7b7b", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    {(gaStatus.properties ?? []).length === 0 ? (
                      <div style={{ fontSize: 12, color: "#7b7b7b" }}>No GA4 properties found in this account.</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {(gaStatus.properties ?? []).map(prop => {
                          const isSelected = prop.property_id === gaStatus.selected_property_id;
                          return (
                            <button
                              key={prop.property_id}
                              disabled={gaSelectingProperty}
                              onClick={async () => {
                                if (!supabase || gaSelectingProperty) return;
                                setGaSelectingProperty(true);
                                setGaError(null);
                                try {
                                  const { error: selErr } = await supabase.rpc("select_ga_property", {
                                    p_advertiser_id: effectiveAdvertiserId,
                                    p_property_id: prop.property_id,
                                  });
                                  if (selErr) throw selErr;

                                  setGaStatus(prev => ({
                                    ...prev,
                                    selected_property_id: prop.property_id,
                                    properties: (prev.properties ?? []).map(p => ({
                                      ...p,
                                      selected: p.property_id === prop.property_id,
                                    })),
                                  }));
                                  setGaShowPropertySelector(false);
                                } catch (err) {
                                  setGaError(err?.message ?? "Failed to save property selection");
                                } finally {
                                  setGaSelectingProperty(false);
                                }
                              }}
                              style={{
                                textAlign: "left", padding: "9px 12px", borderRadius: 8,
                                border: `1px solid ${isSelected ? "#F90091" : "#E2E2E2"}`,
                                background: isSelected ? "#FFF5FB" : "#FAFAFA",
                                cursor: gaSelectingProperty ? "default" : "pointer",
                                opacity: gaSelectingProperty ? 0.6 : 1,
                              }}
                            >
                              <div style={{ fontSize: 12, fontWeight: 600, color: isSelected ? "#F90091" : "#151515" }}>
                                {prop.property_display_name ?? prop.property_id}
                              </div>
                              {prop.account_display_name && (
                                <div style={{ fontSize: 10, color: "#7b7b7b", marginTop: 2 }}>
                                  {prop.account_display_name} · {prop.property_id}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : gaStatus.selected_property_id ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: "#7b7b7b", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 }}>Active Property</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#151515", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {gaStatus.properties?.find(p => p.property_id === gaStatus.selected_property_id)?.property_display_name ?? gaStatus.selected_property_id}
                      </div>
                      <div style={{ fontSize: 10, color: "#7b7b7b", marginTop: 1 }}>{gaStatus.selected_property_id}</div>
                    </div>
                    <button
                      disabled={gaRefreshing}
                      onClick={async () => {
                        setGaRefreshing(true);
                        try {
                          const properties = await gaRefreshProperties(effectiveAdvertiserId, session?.access_token);
                          setGaStatus(prev => ({ ...prev, properties }));
                        } catch { /* show stale list on error */ }
                        finally {
                          setGaRefreshing(false);
                          setGaShowPropertySelector(true);
                        }
                      }}
                      style={{ padding: "6px 10px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: gaRefreshing ? "default" : "pointer", background: gaRefreshing ? "#E2E2E2" : "#151515", color: gaRefreshing ? "#B8B8B8" : "#fff", flexShrink: 0 }}
                    >
                      {gaRefreshing ? "Loading…" : "Change"}
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "#7b7b7b" }}>No property selected. Pick one to activate the integration.</div>
                )}
              </div>
            )}
          </div>

          {/* 3rd-Party Tracking Pixel card */}
          {pixelError && !pixelEditing && (
            <div style={{ marginTop: 12, padding: "8px 10px", background: "#FFF0F0", border: "1px solid #fca5a5", borderRadius: 8, color: "#e53935", fontSize: 12 }}>
              {pixelError}
            </div>
          )}
          <div style={{
            marginTop: 12,
            background: "#FFFFFF",
            border: `1px solid ${pixelUrl && !pixelEditing ? "#F9009133" : "#E2E2E2"}`,
            borderRadius: 12,
            overflow: "hidden",
          }}>
            <div style={{ padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 10,
                background: "#FFF5FB", border: "1px solid #F9009133",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#F90091" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="3" fill="#F90091"/>
                </svg>
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, color: "#151515", fontSize: 14, lineHeight: 1.2 }}>3rd-Party Tracking Pixel</span>
                  <span style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase",
                    color: "#7b7b7b", background: "#F5F5F5", padding: "2px 6px", borderRadius: 4,
                  }}>Tracking</span>
                </div>
                {pixelLoading ? (
                  <div style={{ height: 14, width: 120, background: "#F0F0F0", borderRadius: 4 }} />
                ) : pixelUrl && !pixelEditing ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", display: "inline-block", flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#3E3E3E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {pixelUrl}
                    </span>
                  </div>
                ) : !pixelEditing ? (
                  <span style={{ fontSize: 12, color: "#7b7b7b" }}>Not configured</span>
                ) : null}
              </div>

              {!pixelEditing && (
                <div style={{ flexShrink: 0, paddingTop: 2, display: "flex", gap: 6 }}>
                  {pixelLoading ? (
                    <div style={{ width: 90, height: 30, background: "#F0F0F0", borderRadius: 8 }} />
                  ) : pixelUrl ? (
                    <>
                      <button
                        onClick={startPixelEdit}
                        disabled={pixelConfirmingRemove}
                        style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E2E2", fontSize: 12, fontWeight: 500, cursor: pixelConfirmingRemove ? "default" : "pointer", background: "#FAFAFA", color: "#3E3E3E", opacity: pixelConfirmingRemove ? 0.6 : 1, whiteSpace: "nowrap" }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setPixelConfirmingRemove(true)}
                        disabled={pixelSavingState === "saving" || pixelConfirmingRemove}
                        style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #151515", fontSize: 12, fontWeight: 600, cursor: pixelSavingState === "saving" ? "default" : "pointer", background: "none", color: "#151515", opacity: pixelSavingState === "saving" || pixelConfirmingRemove ? 0.6 : 1, whiteSpace: "nowrap" }}
                      >
                        {pixelSavingState === "saving" ? "Removing…" : "Remove"}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={startPixelEdit}
                      style={{ padding: "6px 14px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", background: "#F90091", color: "#fff", whiteSpace: "nowrap" }}
                    >
                      Add pixel
                    </button>
                  )}
                </div>
              )}
            </div>

            {pixelConfirmingRemove && (
              <div style={{ borderTop: "1px solid #F0F0F0", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "#FFF5F5" }}>
                <span style={{ fontSize: 12, color: "#7b1e1c" }}>Remove this tracking pixel?</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => setPixelConfirmingRemove(false)}
                    style={{ padding: "5px 10px", background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 6, fontSize: 11, cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={removePixel}
                    disabled={pixelSavingState === "saving"}
                    style={{ padding: "5px 10px", background: "#e53935", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: pixelSavingState === "saving" ? "default" : "pointer" }}
                  >
                    {pixelSavingState === "saving" ? "Removing…" : "Remove"}
                  </button>
                </div>
              </div>
            )}

            {!pixelLoading && !pixelUrl && !pixelEditing && (
              <div style={{ borderTop: "1px solid #F0F0F0", padding: "10px 16px" }}>
                <span style={{ fontSize: 12, color: "#7b7b7b", lineHeight: 1.5 }}>
                  Add a 3rd-party pixel URL (e.g. Podscribe) to automatically track events across your creatives.
                </span>
              </div>
            )}

            {pixelEditing && (
                <div style={{ borderTop: "1px solid #F0F0F0", padding: "12px 16px" }}>
                  <input
                    autoFocus
                    aria-label="Tracking pixel URL"
                    value={pixelDraft}
                    onChange={(e) => setPixelDraft(e.target.value)}
                    placeholder="https://tracking.podscribe.com/px?id={{IMPRESSION_ID}}"
                    maxLength={2048}
                    style={{ width: "100%", boxSizing: "border-box", background: "#FAFAFA", border: "1px solid #E2E2E2", borderRadius: 8, padding: "9px 12px", color: "#151515", fontSize: 13, outline: "none" }}
                  />
                  {trimmedPixelDraft && pixelValidation.validMacros.length > 0 && (
                    <p style={{ fontSize: 12, color: "#F90091", marginTop: 6 }}>
                      Detected macros: {pixelValidation.validMacros.join(", ")}
                    </p>
                  )}
                  {trimmedPixelDraft && (pixelValidation.hasError || pixelValidation.hasErrorWarning) && (
                    <p style={{ fontSize: 12, marginTop: 6, color: pixelValidation.hasError ? "#e53935" : "#F59E0B" }}>
                      {pixelValidation.errorMessage}
                    </p>
                  )}
                  {pixelError && (
                    <div style={{ padding: "10px 12px", marginTop: 10, background: "#FFF0F0", border: "1px solid #fca5a5", borderRadius: 8, color: "#e53935", fontSize: 12 }}>{pixelError}</div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button
                      type="button"
                      onClick={cancelPixelEdit}
                      style={{ flex: 1, padding: "10px 0", background: "#F5F5F5", color: "#3E3E3E", border: "1px solid #E2E2E2", borderRadius: 8, fontSize: 13, cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={savePixel}
                      disabled={pixelSavingState === "saving" || !trimmedPixelDraft || pixelValidation.hasError}
                      style={{ flex: 1, padding: "10px 0", background: "#F90091", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: pixelSavingState === "saving" ? "default" : "pointer", opacity: pixelSavingState === "saving" || !trimmedPixelDraft || pixelValidation.hasError ? 0.6 : 1 }}
                    >
                      {pixelSavingState === "saving" ? "Saving…" : "Save Pixel"}
                    </button>
                  </div>
                </div>
            )}
          </div>

          <div style={{
            marginTop: 12,
            background: "#FFFFFF",
            border: "1px solid #E2E2E2",
            borderRadius: 12,
            overflow: "hidden",
          }}>
            <div style={{ padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 10,
                background: "#FFF5FB", border: "1px solid #F9009133",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="#F90091" strokeWidth="2"/>
                  <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="#F90091" strokeWidth="2"/>
                  <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="#F90091" strokeWidth="2"/>
                  <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="#F90091" strokeWidth="2"/>
                </svg>
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, color: "#151515", fontSize: 14, lineHeight: 1.2 }}>Affiliate Platform Reporting</span>
                  <span style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase",
                    color: "#7b7b7b", background: "#F5F5F5", padding: "2px 6px", borderRadius: 4,
                  }}>Reporting</span>
                </div>
                {affiliateReportingLoading ? (
                  <div style={{ height: 14, width: 120, background: "#F0F0F0", borderRadius: 4 }} />
                ) : !affiliateReportingEditing && affiliateReportingSavedPlatform ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", display: "inline-block", flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#3E3E3E" }}>{affiliateReportingSavedPlatform}</span>
                  </div>
                ) : !affiliateReportingEditing ? (
                  <span style={{ fontSize: 12, color: "#7b7b7b" }}>Not configured</span>
                ) : (
                  <span style={{ fontSize: 12, color: "#7b7b7b" }}>Choose which affiliate platform receives your CPA reports.</span>
                )}
              </div>

              {!affiliateReportingLoading && !affiliateReportingEditing && (canManageBilling || isAdvertiserAdmin) && (
                <div style={{ flexShrink: 0, paddingTop: 2 }}>
                  <button
                    type="button"
                    onClick={startAffiliateReportingEdit}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E2E2", fontSize: 12, fontWeight: 500, cursor: "pointer", background: "#FAFAFA", color: "#3E3E3E", whiteSpace: "nowrap" }}
                  >
                    {affiliateReportingSavedPlatform ? "Edit" : "Set up"}
                  </button>
                </div>
              )}
            </div>

            {affiliateReportingEditing && (
              <div style={{ borderTop: "1px solid #F0F0F0", padding: "12px 16px" }}>
                <select
                  aria-label="Affiliate reporting platform"
                  value={affiliateReportingPlatform}
                  onChange={(e) => setAffiliateReportingPlatform(e.target.value)}
                  disabled={affiliateReportingSaving}
                  style={{
                    width: "100%", padding: "9px 32px 9px 12px",
                    background: `#FAFAFA url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%23626262' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M1 1.5L6 6.5L11 1.5'/></svg>") no-repeat right 12px center / 10px 6px`,
                    border: "1px solid #E2E2E2", borderRadius: 8, color: "#151515", fontSize: 13, fontFamily: "inherit", outline: "none",
                    appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
                    cursor: affiliateReportingSaving ? "not-allowed" : "pointer",
                  }}
                >
                  <option value="">Select platform…</option>
                  {affiliateReportingPlatforms.map((platform) => (
                    <option key={platform} value={platform}>{platform}</option>
                  ))}
                </select>

                {affiliateReportingError && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#e53935" }}>{affiliateReportingError}</div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={cancelAffiliateReportingEdit}
                    disabled={affiliateReportingSaving}
                    style={{ flex: 1, padding: "10px 0", background: "#F5F5F5", color: "#3E3E3E", border: "1px solid #E2E2E2", borderRadius: 8, fontSize: 13, cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveAffiliateReportingPlatform}
                    disabled={affiliateReportingSaving}
                    style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 600, cursor: affiliateReportingSaving ? "default" : "pointer", background: "#F90091", color: "#fff", opacity: affiliateReportingSaving ? 0.6 : 1 }}
                  >
                    {affiliateReportingSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )
    },
    settings: {
      title: "Settings",
      content: (
        <div>
          {settingsLoading ? (
            <div style={{ color:"#626262", fontSize:13, padding:"12px 0" }}>Loading…</div>
          ) : (
            <>
              {/* Pending review indicator */}
              {pendingRow && (
                <div style={{ marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
                  <PendingChip label="Pending review" />
                </div>
              )}

              {/* Advertiser UUID */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Advertiser UUID</div>
                <div style={{ display:"flex", gap:8 }}>
                  <input
                    value={advertiserUuid || ""}
                    readOnly
                    placeholder="UUID unavailable"
                    style={{ flex:1, boxSizing:"border-box", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"9px 12px", color:"#151515", fontSize:13, outline:"none", fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace" }}
                  />
                  <button
                    type="button"
                    aria-label="Copy advertiser UUID"
                    onClick={handleCopyAdvertiserUuid}
                    disabled={!advertiserUuid}
                    style={{ width:36, height:36, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", background:"#FFF5FB", border:"1px solid #F9009133", borderRadius:8, color:"#F90091", fontSize:14, fontWeight:700, cursor: advertiserUuid ? "pointer" : "not-allowed", opacity: advertiserUuid ? 1 : 0.5 }}
                  >
                    {uuidCopyState === "copied" ? "✓" : "⧉"}
                  </button>
                </div>
              </div>

              {/* Company name */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Company Name</div>
                <input
                  value={draft.name}
                  onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                  style={{ width:"100%", boxSizing:"border-box", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"9px 12px", color:"#151515", fontSize:13, outline:"none" }}
                />
              </div>

              {/* Website */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Website</div>
                <input
                  value={draft.website}
                  onChange={e => { setDraft(d => ({ ...d, website: e.target.value })); setSuggestions([]); }}
                  onBlur={() => {
                    const normalized = ensureHttpsUrl(draft.website);
                    if (normalized !== draft.website) {
                      setDraft((d) => ({ ...d, website: normalized }));
                    }
                  }}
                  placeholder="https://yourbrand.com"
                  style={{ width:"100%", boxSizing:"border-box", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"9px 12px", color:"#151515", fontSize:13, outline:"none" }}
                />
                {/* Category suggestion chips */}
                {suggestions.length > 0 && (
                  <div style={{ marginTop:8, display:"flex", flexWrap:"wrap", gap:6 }}>
                    {suggestions.map(s => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => { setDraft(d => ({ ...d, category: s.value })); setCategoryInput(s.title); setSuggestions([]); }}
                        style={{ padding:"4px 12px", background:"#FFF5FB", border:"1px solid #F9009133", color:"#F90091", borderRadius:20, fontSize:12, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}
                      >
                        {s.title}
                        <span style={{ opacity:0.6, fontWeight:400 }}>{Math.round(s.confidence * 100)}%</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Category combobox */}
              <div style={{ marginBottom:20, position:"relative" }}>
                <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Category</div>
                <div style={{ display:"flex", gap:8 }}>
                  <input
                    value={categoryInput}
                    onFocus={() => setShowCategoryDropdown(true)}
                    onChange={e => {
                      setCategoryInput(e.target.value);
                      setShowCategoryDropdown(true);
                    }}
                    onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 150)}
                    placeholder="Search categories…"
                    style={{ flex:1, boxSizing:"border-box", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"9px 12px", color:"#151515", fontSize:13, outline:"none" }}
                  />
                  <button
                    type="button"
                    disabled={!draft.website?.trim() || suggestLoading}
                    onClick={async () => {
                      const url = ensureHttpsUrl(draft.website);
                      if (!url || !supabase) return;
                      if (url !== draft.website) {
                        setDraft((d) => ({ ...d, website: url }));
                      }
                      setSuggestLoading(true);
                      setSuggestions([]);
                      try {
                        const { data, error } = await supabase.functions.invoke("suggest-advertiser-category", { body: { url } });
                        setSuggestions((!error && data?.suggestions) ? data.suggestions : []);
                      } catch { setSuggestions([]); }
                      finally { setSuggestLoading(false); }
                    }}
                    style={{ flexShrink:0, padding:"9px 12px", background:"#FFF5FB", border:"1px solid #F9009133", borderRadius:8, color:"#F90091", fontSize:12, fontWeight:600, cursor: !draft.website?.trim() || suggestLoading ? "not-allowed" : "pointer", opacity: !draft.website?.trim() || suggestLoading ? 0.5 : 1, whiteSpace:"nowrap" }}
                  >
                    {suggestLoading ? "…" : "✦ Suggest"}
                  </button>
                </div>
                {showCategoryDropdown && (() => {
                  const q = categoryInput.toLowerCase();
                  const filtered = COMPANY_CATEGORIES.filter(c => !q || c.title.toLowerCase().includes(q));
                  return (
                    <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0, background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, boxShadow:"0 8px 24px rgba(0,0,0,0.12)", zIndex:100, maxHeight:200, overflowY:"auto" }}>
                      {filtered.length === 0 && (
                        <div style={{ padding:"10px 12px", color:"#B8B8B8", fontSize:12 }}>No matches</div>
                      )}
                      {filtered.map(c => (
                        <button
                          key={c.value}
                          type="button"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => {
                            setCategoryInput(c.title);
                            setDraft(d => ({ ...d, category: c.value }));
                            setShowCategoryDropdown(false);
                          }}
                          style={{ display:"block", width:"100%", textAlign:"left", padding:"8px 12px", background: draft.category === c.value ? "#FFF5FB" : "transparent", border:"none", borderBottom:"1px solid #F0F0F0", color: draft.category === c.value ? "#F90091" : "#3E3E3E", fontSize:13, cursor:"pointer", fontWeight: draft.category === c.value ? 600 : 400 }}
                        >
                          {c.title}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {saveError && (
                <div style={{ padding:"10px 12px", marginBottom:12, background:"#FFF0F0", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:12 }}>{saveError}</div>
              )}

              <button
                onClick={handleSettingsSave}
                disabled={savingState === "saving"}
                style={{ width:"100%", padding:"10px 0", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor: savingState === "saving" ? "default" : "pointer", opacity: savingState === "saving" ? 0.7 : 1 }}
              >
                {savingState === "saving" ? "Saving…" : savingState === "saved" ? "Submitted!" : "Save Changes"}
              </button>
            </>
          )}
        </div>
      )
    },
  };

  return (
    <>
      {/* Avatar button */}
      <div ref={menuRef} style={{ position:"relative", flexShrink:0, paddingLeft:12, alignSelf:"center" }}>
        <div onClick={() => setOpen(!open)} style={{ width:35, height:35, borderRadius:"50%", background:"#534f60", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:"#fff", cursor:"pointer", fontWeight:600, lineHeight:1, userSelect:"none" }}>{(displayName || "U").charAt(0).toUpperCase()}</div>

        {open && !screen && (
          <div style={{ position:"absolute", top:"calc(100% + 8px)", right:0, background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:10, zIndex:100, minWidth:280, boxShadow:"0 3px 16px rgba(0,0,0,0.10)", overflow:"hidden" }}>
            {/* User header */}
            <div style={{ padding:"20px 30px 14px" }}>
              <div style={{ fontWeight:600, color:"#151515", fontSize:20, fontFamily:"'Clash Display Variable','Clash Display',sans-serif", lineHeight:1.2, marginBottom:4 }}>{displayName}</div>
              <div style={{ color:"rgba(21,21,21,0.7)", fontSize:16 }}>{displayEmail || "—"}</div>
            </div>
            <div style={{ height:1, background:"#E2E2E2", margin:"0 30px" }} />

            {/* Account switcher */}
            {advertisers.length > 1 && masqueradeAdvertiserId == null && (
              <>
                <div style={{ padding:"14px 30px 6px" }}>
                  <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Switch account</div>
                  {advertisers.map((a) => {
                    const active = a.id === currentAdvertiserId;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setCurrentAdvertiserId(a.id)}
                        style={{
                          display:"flex", alignItems:"center", justifyContent:"space-between", gap:10,
                          width:"100%", textAlign:"left", padding:"8px 0",
                          background:"transparent", border:"none",
                          color: active ? "#F90091" : "#3E3E3E",
                          fontSize:15, cursor:"pointer",
                        }}
                      >
                        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.name}</span>
                        {active && <span style={{ fontSize:12, color:"#F90091" }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
                <div style={{ height:1, background:"#E2E2E2", margin:"0 30px" }} />
              </>
            )}

            {/* Menu items */}
            <div style={{ padding:"20px 30px" }}>
              <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
                {menuItems.map((item, idx) => (
                  <React.Fragment key={item.key}>
                    <button
                      type="button"
                      disabled={!!item.comingSoon}
                      onClick={() => {
                        if (item.comingSoon) return;
                        if (item.key === "signout") { setOpen(false); authSignOut(); return; }
                        if (item.key === "profile") { setNameInput(displayName); setNameError(null); }
                        setScreen(item.key);
                      }}
                      style={{
                        display:"flex", alignItems:"center", gap:14, width:"100%", textAlign:"left",
                        background:"transparent", border:"none", padding:0,
                        color: item.danger ? "#e53935" : item.comingSoon ? "rgba(21,21,21,0.35)" : "rgba(21,21,21,0.7)",
                        fontSize:16, cursor: item.comingSoon ? "not-allowed" : "pointer",
                      }}
                    >
                      <span style={{ flexShrink:0, color:"inherit" }}>{item.icon}</span>
                      <span style={{ flex:1 }}>{item.label}</span>
                      {item.comingSoon && <span style={{ fontSize:10, color:"rgba(21,21,21,0.35)", background:"#F5F5F5", padding:"2px 7px", borderRadius:20 }}>Soon</span>}
                    </button>
                    {idx < menuItems.length - 1 && (
                      <div style={{ height:1, background:"#E2E2E2" }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Panel modal — rendered via portal to escape nav's backdrop-filter stacking context */}
      {open && screen && panels[screen] && ReactDOM.createPortal(
        <div ref={panelRef} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", zIndex:200, display:"flex", justifyContent:"flex-end" }}>
          <div style={{ width:"100%", maxWidth:380, background:"#FFFFFF", borderLeft:"1px solid #E2E2E2", display:"flex", flexDirection:"column", height:"100%" }}>
            <div style={{ padding:"20px 20px 16px", borderBottom:"1px solid #E2E2E2", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <button onClick={() => setScreen(null)} style={{ background:"none", border:"none", color:"#626262", fontSize:13, cursor:"pointer" }}>← Back</button>
              <div style={{ fontWeight:600, color:"#151515", fontSize:15, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>{panels[screen].title}</div>
              <button onClick={() => { setScreen(null); setOpen(false); }} style={{ background:"none", border:"none", color:"#626262", fontSize:18, cursor:"pointer" }}>✕</button>
            </div>
            <div style={{ flex:1, overflowY:"auto", padding:20 }}>
              {panels[screen].content}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
